import React from 'react';
import { Type, ImageIcon, Video, FileText, MapPin, GalleryHorizontal, KeyRound } from 'lucide-react';
import clsx from 'clsx';
import { Template } from '@/services/templates.service';

interface Props {
  template: Pick<Template, 'type' | 'category' | 'headerType' | 'cards'>;
  size?: 'sm' | 'md';
}

interface BadgeMeta {
  label: string;
  icon: React.ReactNode;
  className: string;
}

/**
 * Picks a single badge that summarises what kind of message a template
 * produces — carousel, image, video, document, location, plain text, or OTP.
 * Carousel takes priority since it implies the cards control the visuals.
 */
function pickMeta(t: Props['template']): BadgeMeta {
  if (t.type === 'CAROUSEL') {
    const cardCount = t.cards?.length ?? 0;
    return {
      label: cardCount > 0 ? `Carousel · ${cardCount}` : 'Carousel',
      icon: <GalleryHorizontal size={11} />,
      className: 'bg-surface-alt text-text-secondary',
    };
  }
  if (t.category === 'authentication') {
    return { label: 'OTP', icon: <KeyRound size={11} />, className: 'bg-surface-alt text-text-secondary' };
  }
  switch (t.headerType) {
    case 'IMAGE':    return { label: 'Image',    icon: <ImageIcon size={11} />, className: 'bg-surface-alt text-text-secondary' };
    case 'VIDEO':    return { label: 'Video',    icon: <Video size={11} />,     className: 'bg-surface-alt text-text-secondary' };
    case 'DOCUMENT': return { label: 'Document', icon: <FileText size={11} />,  className: 'bg-surface-alt text-text-secondary' };
    case 'LOCATION': return { label: 'Location', icon: <MapPin size={11} />,    className: 'bg-surface-alt text-text-secondary' };
    case 'TEXT':
    case 'NONE':
    default:
      return { label: 'Text', icon: <Type size={11} />, className: 'bg-surface-alt text-text-secondary' };
  }
}

export const FormatBadge: React.FC<Props> = ({ template, size = 'sm' }) => {
  const meta = pickMeta(template);
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 rounded-full font-medium',
      meta.className,
      size === 'sm' ? 'px-2 py-0.5 text-2xs' : 'px-2.5 py-1 text-xs',
    )}>
      {meta.icon}
      {meta.label}
    </span>
  );
};
